/**
 * Client Configuration Context
 *
 * Provides client-specific configuration throughout the application
 * Configuration is based on REACT_APP_CLIENT environment variable
 */

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getClientConfig } from '../config/clients';

const ClientConfigContext = createContext(null);

export const ClientConfigProvider = ({ children }) => {
  const config = useMemo(() => getClientConfig(), []);

  /**
   * Get a specific configuration value by path
   * Example: getValue('currency.code') returns 'KM' or 'EUR'
   */
  const getValue = useCallback(
    (path, defaultValue = null) => {
      const keys = path.split('.');
      let value = config;

      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return defaultValue;
        }
      }

      return value;
    },
    [config],
  );

  /**
   * Format currency value with client-specific settings
   */
  const formatCurrency = useCallback(
    (value, precision = null) => {
      if (value === null || value === undefined) {
        return `0.00 ${config.currency.code}`;
      }

      const decimals =
        precision !== null ? precision : config.currency.decimalPlaces;
      const formatted = Number(value).toFixed(decimals);

      return `${formatted} ${config.currency.code}`;
    },
    [config.currency.code, config.currency.decimalPlaces],
  );

  /**
   * Format currency for display with custom separators
   */
  const formatCurrencyAdvanced = useCallback(
    (value, precision = null) => {
      if (value === null || value === undefined) {
        return `0.00 ${config.currency.code}`;
      }

      const decimals =
        precision !== null ? precision : config.currency.decimalPlaces;
      const number = Number(value);

      // Split into integer and decimal parts
      const parts = number.toFixed(decimals).split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Add thousand separators
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        config.currency.thousandSeparator,
      );

      const formattedValue = decimalPart
        ? `${formattedInteger}${config.currency.decimalSeparator}${decimalPart}`
        : formattedInteger;

      return `${formattedValue} ${config.currency.code}`;
    },
    [
      config.currency.code,
      config.currency.decimalPlaces,
      config.currency.thousandSeparator,
      config.currency.decimalSeparator,
    ],
  );

  /**
   * Calculate PDV (tax) amount
   */
  const calculatePDV = useCallback(
    (amount) => {
      const pdvRate = config.tax.pdv / 100;
      return amount * pdvRate;
    },
    [config.tax.pdv],
  );

  /**
   * Calculate amount with PDV included
   */
  const calculateWithPDV = useCallback(
    (amount) => {
      const pdvRate = config.tax.pdv / 100;
      return amount * (1 + pdvRate);
    },
    [config.tax.pdv],
  );

  /**
   * Calculate amount without PDV (base amount)
   */
  const calculateWithoutPDV = useCallback(
    (amountWithPDV) => {
      const pdvRate = config.tax.pdv / 100;
      return amountWithPDV / (1 + pdvRate);
    },
    [config.tax.pdv],
  );

  /**
   * Get PDV percentage
   */
  const getPDVPercentage = useCallback(() => {
    return config.tax.pdv;
  }, [config.tax.pdv]);

  /**
   * Get PDV label
   */
  const getPDVLabel = useCallback(() => {
    return config.tax.label;
  }, [config.tax.label]);

  const contextValue = useMemo(
    () => ({
      // Full config object
      config,

      // Helper functions
      getValue,
      formatCurrency,
      formatCurrencyAdvanced,
      calculatePDV,
      calculateWithPDV,
      calculateWithoutPDV,
      getPDVPercentage,
      getPDVLabel,

      // Quick access to common values
      currency: config.currency.code,
      currencySymbol: config.currency.symbol,
      pdvPercentage: config.tax.pdv,
      pdvLabel: config.tax.label,
      clientId: config.clientId,
      clientName: config.clientName,
      features: config.features || {},
    }),
    [
      config,
      getValue,
      formatCurrency,
      formatCurrencyAdvanced,
      calculatePDV,
      calculateWithPDV,
      calculateWithoutPDV,
      getPDVPercentage,
      getPDVLabel,
    ],
  );

  return (
    <ClientConfigContext.Provider value={contextValue}>
      {children}
    </ClientConfigContext.Provider>
  );
};

/**
 * Hook to access client configuration
 * @returns {Object} Client configuration and helper functions
 */
export const useClientConfig = () => {
  const context = useContext(ClientConfigContext);

  if (!context) {
    throw new Error(
      'useClientConfig must be used within a ClientConfigProvider',
    );
  }

  return context;
};

export default ClientConfigContext;
